import { useMemo } from "react";
import { useGetIncomeTotal } from "../../api/api.get";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { toFixedDisplay } from "../../utils/number.utils";

/**
 * Estimated Savings = derived monthly income - monthly expenses.
 * This is a projection and is intentionally NOT the same as the Savings
 * module (which holds manually recorded savings entries). Keep the
 * "Estimated" qualifier in the copy so the two are never conflated.
 */
export default function EstimatedSavingsCard({
  expenseTotal,
}: {
  expenseTotal?: number;
}) {
  const { defaultGroupId } = useGroupContext();
  const { data } = useGetIncomeTotal(defaultGroupId);

  const { currency } = useCurrencyContext();

  const hasIncome = !!data?.total;

  // Income is stored as an annual amount, so derive the monthly figure.
  const monthlyIncome = useMemo(
    () => (data?.total ? data.total / 12 : 0),
    [data],
  );

  const estimatedSavings = useMemo(
    () => Math.max(monthlyIncome - (expenseTotal ?? 0), 0),
    [monthlyIncome, expenseTotal],
  );

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Estimated Savings
        </CardTitle>
        <CardDescription>Based on your income and expenses</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="border-b pb-4">
            <p className="text-sm text-muted-foreground mb-1">
              Estimated Monthly Savings
            </p>
            <p className="text-3xl font-bold text-primary">
              {currency}
              {toFixedDisplay(hasIncome ? estimatedSavings : 0)}
            </p>
            {!hasIncome && (
              <p className="text-xs text-muted-foreground mt-2">
                Set income to see estimated savings.
              </p>
            )}
          </div>

          {hasIncome && (
            <div className="border-b pb-4">
              <p className="text-sm text-muted-foreground mb-1">
                Monthly Income
              </p>
              <p className="text-3xl font-bold text-foreground">
                {currency}
                {toFixedDisplay(monthlyIncome)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
